import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Tooltip,
} from '@mui/material';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import CreateNewFolderRoundedIcon from '@mui/icons-material/CreateNewFolderRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Folder, reorderFolders, deleteFolder } from '../../services/folders_api';
import { FolderGlyph, folderColor } from './folderVisuals';
import FolderEditDialog from './FolderEditDialog';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import { useQueryClient } from '@tanstack/react-query';
import { folderCache } from '../../services/folders_cache';
import { useUser } from '../../contexts/UserContext';
import { getFolderMaxCount, FOLDER_COUNT_LIMITS, FOLDER_ITEM_LIMITS, isSubscribed } from '../../utils/subscription';
import PlanUpgradeCTA from '../common/PlanUpgradeCTA';

interface FolderManageDialogProps {
  open: boolean;
  onClose: () => void;
  folders: Folder[];
  /** フォルダ削除時に通知（選択中フォルダのリセット用） */
  onFolderDeleted?: (id: number) => void;
}

interface RowProps {
  folder: Folder;
  onEdit: (f: Folder) => void;
  onDelete: (f: Folder) => void;
}

const SortableFolderRow: React.FC<RowProps> = ({ folder, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: folder.id,
  });
  const color = folderColor(folder.color);

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.85,
        borderRadius: '10px',
        backgroundColor: isDragging ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.05)',
        boxShadow: isDragging ? '0 6px 20px rgba(0,0,0,0.4)' : 'none',
        position: 'relative',
        zIndex: isDragging ? 1 : 0,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: 'rgba(255,255,255,0.25)',
          touchAction: 'none',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorRoundedIcon sx={{ fontSize: '1.1rem' }} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          borderRadius: '8px',
          backgroundColor: `${color}1f`,
          flexShrink: 0,
        }}
      >
        <FolderGlyph iconKey={folder.icon} sx={{ fontSize: '1.05rem', color }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {folder.name}
        </Typography>
        <Typography sx={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.3 }}>
          {folder.item_count} 本
        </Typography>
      </Box>

      <Tooltip title={folder.visibility === 1 ? '公開' : '非公開'} arrow>
        {folder.visibility === 1 ? (
          <PublicRoundedIcon sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.4)' }} />
        ) : (
          <LockRoundedIcon sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.25)' }} />
        )}
      </Tooltip>

      <Box
        onClick={() => onEdit(folder)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, borderRadius: '6px', cursor: 'pointer',
          color: 'rgba(255,255,255,0.4)',
          '&:hover': { color: 'text.primary', backgroundColor: 'rgba(255,255,255,0.06)' },
        }}
      >
        <EditRoundedIcon sx={{ fontSize: '0.95rem' }} />
      </Box>
      <Box
        onClick={() => onDelete(folder)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, borderRadius: '6px', cursor: 'pointer',
          color: 'rgba(255,255,255,0.4)',
          '&:hover': { color: 'error.main', backgroundColor: 'rgba(244,67,54,0.08)' },
        }}
      >
        <DeleteOutlineRoundedIcon sx={{ fontSize: '1rem' }} />
      </Box>
    </Box>
  );
};

// フォルダの一覧管理：並べ替え・編集・削除・新規作成。
const FolderManageDialog: React.FC<FolderManageDialogProps> = ({ open, onClose, folders, onFolderDeleted }) => {
  const [items, setItems] = useState<Folder[]>(folders);
  const [editTarget, setEditTarget] = useState<Folder | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Folder | null>(null);
  const queryClient = useQueryClient();
  const { user } = useUser();
  const plan = user?.subscription_plan ?? 0;
  const maxFolders = getFolderMaxCount(plan);
  const isFree = !isSubscribed(plan);

  useEffect(() => {
    setItems(folders);
  }, [folders]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    try {
      await reorderFolders(next.map((f) => f.id));
      folderCache.reorder(queryClient, next);
    } catch (e) {
      console.error('フォルダの並べ替えに失敗しました', e);
      setItems(folders); // 即時ロールバック
      folderCache.invalidate(queryClient); // 真値を取り直す
    }
  };

  const handleCreate = () => {
    setEditTarget(null);
    setEditOpen(true);
  };
  const handleEdit = (f: Folder) => {
    setEditTarget(f);
    setEditOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    try {
      await deleteFolder(id);
      folderCache.remove(queryClient, id);
      onFolderDeleted?.(id);
    } catch (e) {
      console.error('フォルダの削除に失敗しました', e);
      folderCache.invalidate(queryClient);
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'baseline', gap: 1 }}
        >
          フォルダを管理
          <Typography component="span" sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 500 }}>
            {items.length} / {maxFolders}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* 無料プランのお試し枠を案内 */}
          {isFree && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.85,
                mb: 1.25,
                borderRadius: '8px',
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: '0.95rem', color: 'text.secondary', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.5 }}>
                無料プランではフォルダ {FOLDER_COUNT_LIMITS.free} 個（各フォルダに{FOLDER_ITEM_LIMITS.free}件）までお試しいただけます
              </Typography>
            </Box>
          )}
          {items.length === 0 ? (
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', py: 3, textAlign: 'center' }}>
              フォルダがありません。作成しましょう。
            </Typography>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {items.map((folder) => (
                    <SortableFolderRow
                      key={folder.id}
                      folder={folder}
                      onEdit={handleEdit}
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          )}

          <Button
            onClick={handleCreate}
            startIcon={<CreateNewFolderRoundedIcon />}
            fullWidth
            sx={{
              mt: 1.5,
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: 'rgba(255,255,255,0.55)',
              border: '1px dashed rgba(255,255,255,0.15)',
              borderRadius: '10px',
              py: 1,
              '&:hover': { borderColor: 'rgba(217,255,0,0.4)', color: 'primary.main', backgroundColor: 'rgba(217,255,0,0.03)' },
            }}
          >
            新規フォルダ
          </Button>

          {/* 上限の自然な提示（80%以降に出現） */}
          <Box sx={{ mt: 1 }}>
            <PlanUpgradeCTA
              currentPlan={plan}
              currentValue={items.length}
              limits={FOLDER_COUNT_LIMITS}
              unit="個"
              verb="作れ"
              showMultiplier={false}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>閉じる</Button>
        </DialogActions>
      </Dialog>

      <FolderEditDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        folder={editTarget}
        folderCount={items.length}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="フォルダを削除"
        message={`「${deleteTarget?.name ?? ''}」を削除しますか？フォルダ内のゲーム自体は削除されません。`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        confirmText="削除"
        cancelText="キャンセル"
      />
    </>
  );
};

export default FolderManageDialog;
